import { v3UserStatus, v4UserStatus, internalRegistrationUser, magicLinkUser } from '@goldcast/api/backend';

import { getUserStatus } from '@/services/auth.service.js';
import { getEventUserInfo } from '@/services/eventUserInfoApi';
import { featureFlags, initializeFeatureFlags } from '@/services/featureFlag.service';
import loggerService from '@/services/logger.service';
import { retry } from '@/services/utils/index';

const getDefaultState = () => {
  return {
    authFetchStatus: 'notstarted',
    authName: null,
    authEmail: null,
    accessToken: null,
    rawData: null,
    otpVerificationSuccess: false
  };
};

// Removed during scalability changes as its not really saving anything
// that is not fetched on load anyway
// const state = loadState().session || getDefaultState();
const state = getDefaultState();

const getters = {
  getSessionUser: state => state.sessionUser,
  userAccessToken: state => state.accessToken,
  getAuthEmail: state => state.authEmail,
  getAuthName: state => state.authName,
  isOtpVerificationSuccess: state => state.otpVerificationSuccess,
  serverTimeDelta: (state, getters, rootState) => rootState.timeSync.offset
};

const mutations = {
  setFetchStatus(state, status) {
    state.authFetchStatus = status;
  },

  setSessionUser(state, user) {
    state.sessionUser = user;
  },

  setAccessToken(state, accessToken) {
    state.accessToken = accessToken;
  },

  setAuthEmail(state, email) {
    state.authEmail = email;
  },
  setAuthName(state, payload) {
    state.authName = payload.name;
  },
  // This is added because the legacy code was relying on
  // parsing and saving incomplete responses
  // but the calling code relied on the full response of
  // /core/user/status
  // This meant that removing duplicate calls to the above
  // api, made the calling code fail if only incomplete data
  // was saved here. Instead of parsing and saving the full
  // response I am just dumping the response into this field
  // because if i just save the response as it is now, it will
  // lead to hard to debug bugs in the future when even
  // more things are added to this api's response
  setRawData(state, payload) {
    state.rawData = payload;
  },
  setOtpVerificationSuccess(state, value) {
    state.otpVerificationSuccess = value;
  },
  cleanState(state) {
    Object.assign(state, getDefaultState());
  }
};

// Retry configuration constants
const MAX_RETRIES = 3;
const RETRY_INTERVAL = 500; // milliseconds

export const actions = {
  async getEmailUserStatus(context, payload) {
    if (context.state.authFetchStatus === 'loading') {
      loggerService.info('emailstatus is already being fetched! waiting');
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          context.dispatch('getEmailUserStatus', payload).then(resolve).catch(reject);
        }, 5000);
      });
    }

    // This call will only be made if the authName data is not downloaded yet
    // or its a special call where status_only is not set and the backend
    // is doing some extra processing aside from just returning the data
    if (!context.state.rawData || !payload.status_only || payload.force_reload) {
      context.commit('setFetchStatus', 'loading');
      const isUserLoggedIn = context.rootState.user?.accessToken;
      const apibody = { ...payload, isUserLoggedIn };
      delete apibody.force_reload;

      const currentUsePentestFlow = context.rootState.event.usePentestFlow;
      if (!currentUsePentestFlow && featureFlags.Use_Pentest_flow.value) {
        // Feature flags are initialized but not yet processed, wait a bit
        await new Promise(resolve => setTimeout(resolve, 100));
      } else if (!currentUsePentestFlow && !featureFlags.Use_Pentest_flow.value) {
        // Feature flags might not be initialized yet, ensure they are
        await initializeFeatureFlags().catch(error => {
          loggerService.error('Feature flags initialization failed, using defaults:', error);
        });
      }

      const useCacheUserStatusApi = apibody.isUserLoggedIn;
      const usePentestFlow = context.rootState.event.usePentestFlow;

      if (useCacheUserStatusApi) {
        try {
          if (usePentestFlow) {
            const response = await getEventUserInfo({
              eventId: apibody.event,
              isInitialLoad: !context.state.rawData,
              legacy: () => v4UserStatus({ body: apibody }),
              mapResponse: info => ({
                name: info.name,
                registeredToEvent: info.registeredToEvent,
                isOrganizer: info.isOrganizer,
                isSpecialUser: info.isSpecialUser,
                blocked: info.blocked,
                agendaFavorites: info.agendaFavorites,
                event_organization: info.event_organization
              })
            });
            return modifyUserStatus(context, response);
          } else {
            const response = await v3UserStatus({ body: apibody });
            return modifyUserStatus(context, response);
          }
        } catch (e) {
          loggerService.log('UserStatusCache: error in getEmailUserStatus', e);
          if (!usePentestFlow) {
            return v2UserStatus(context, apibody);
          } else {
            // Retry v4 if pentest flow is enabled
            const response = await retry(() => v4UserStatus({ body: apibody }), MAX_RETRIES, RETRY_INTERVAL);
            return modifyUserStatus(context, response);
          }
        }
      } else {
        return v2UserStatus(context, apibody);
      }
    }
    loggerService.info('status was already downloaded! returning from store');
    // Look at the comment on setRawData mutator for an explanation about this
    // horribleness
    return Promise.resolve({
      authName: context.state.authName,
      ...context.state.rawData
    });
  },
  settingAuthEmail(context, email) {
    const { commit } = context;
    commit('setAuthEmail', email);
  },

  async resendMagicLink(context, { email, eventId }) {
    const payload = {
      email,
      ...(eventId && { event: eventId }),
      force_send: true
    };

    await magicLinkUser({ body: payload });
  },

  async createUser(context, { email, eventID, sendMagicLink, trial = 0, eventUserStatus = null }) {
    try {
      const userData = {
        email,
        ...(eventID && { event_id: eventID }),
        ...(sendMagicLink !== undefined && {
          is_magic: sendMagicLink
        }),
        ...(eventUserStatus && {
          event_user_status: eventUserStatus
        })
      };
      await internalRegistrationUser({ body: userData });
      return true;
    } catch (err) {
      if (err.data && err.statusCode === 400 && err?.data?.id === 'invalid_event') {
        return true;
      }

      if (err.statusCode === 500) {
        if (trial < 4) {
          const r = await context.dispatch('createUser', {
            email,
            eventID,
            firstName,
            lastName,
            trial: trial + 1
          });
          return r;
        }
        return false;
      }

      throw err;
    }
  },
  RESET_SESSION(context) {
    const { commit } = context;
    commit('cleanState');
  },
  getServerTime() {
    return Promise.resolve();
  }
};

export const v2UserStatus = async (context, apibody) => {
  try {
    const usePentestFlow = context.rootState.event.usePentestFlow;
    const response = await getUserStatus(apibody, usePentestFlow);
    return modifyUserStatus(context, response);
  } catch (e) {
    context.commit('setFetchStatus', 'notstarted');
    loggerService.warn('error in getEmailUserStatus', e);
    return null;
  }
};

export const modifyUserStatus = (context, response) => {
  if (response) {
    context.commit('setAuthName', {
      name: response.name ?? null
    });
    // Go to comment on setRawData mutation
    const modifiedResponse = { ...response };
    delete modifiedResponse['providerName'];
    delete modifiedResponse['userStatus'];
    context.commit('setRawData', modifiedResponse);
    context.commit('setFetchStatus', 'loaded');
    return response;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
